import api from "./axios";
import { notifyMintSpaceActivity } from "../utils/mintSpaceRecent";

function afterAnnouncementMutation(mintSpaceId, result) {
  if (mintSpaceId && result?.success !== false) {
    notifyMintSpaceActivity(mintSpaceId);
  }
  return result;
}

// Create a new announcement (type: "announcement" | "post")
// poll shape: { question: string, options: [{ text: string }] }
export const createAnnouncement = async (
  mintSpaceId,
  content,
  image = "",
  video = "",
  campaign = null,
  type = "announcement",
  poll = null
) => {
  try {
    const response = await api.post("/announcements", {
      mintSpaceId,
      content,
      image,
      video,
      campaign,
      type,
      poll,
    });
    return afterAnnouncementMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};
export const voteOnAnnouncementPoll = async (announcementId, optionIndex, mintSpaceId) => {
  try {
    const response = await api.post(`/announcements/${announcementId}/vote`, { optionIndex });
    return afterAnnouncementMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Get announcements for a mint space, optionally filtered by type
export const getAnnouncementsByMintSpace = async (mintSpaceId, type) => {
  try {
    const params = type ? { type } : undefined;
    const response = await api.get(`/announcements/mint-space/${mintSpaceId}`, { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// The home feed: "For you" (every space, ranked) or "following" (people you follow, newest first)
export const getMyAnnouncementsFeed = async (feed) => {
  try {
    const params = feed === "following" ? { feed } : undefined;
    const response = await api.get("/announcements/my-feed", { params });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Like/unlike an announcement
export const toggleAnnouncementLike = async (announcementId, mintSpaceId) => {
  try {
    const response = await api.post(`/announcements/${announcementId}/like`);
    return afterAnnouncementMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Get one post, e.g. the one a notification points at
export const getAnnouncement = async (announcementId) => {
  try {
    const response = await api.get(`/announcements/${announcementId}`);
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Add a comment to an announcement — or, with parentId, a reply to one of its comments
export const addAnnouncementComment = async (announcementId, content, mintSpaceId, parentId = null) => {
  try {
    const response = await api.post(`/announcements/${announcementId}/comment`, {
      content,
      ...(parentId ? { parentId } : {}),
    });
    return afterAnnouncementMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Like/unlike a comment
export const toggleCommentLike = async (announcementId, commentId, mintSpaceId) => {
  try {
    const response = await api.post(`/announcements/${announcementId}/comments/${commentId}/like`);
    return afterAnnouncementMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Pin/unpin a comment (Mint Space owner and moderators)
export const toggleCommentPin = async (announcementId, commentId, mintSpaceId) => {
  try {
    const response = await api.post(`/announcements/${announcementId}/comments/${commentId}/pin`);
    return afterAnnouncementMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};

// People to suggest after an "@" in a comment on this post
export const getMentionCandidates = async (announcementId, q = "") => {
  try {
    const response = await api.get(`/announcements/${announcementId}/mention-candidates`, {
      params: { q },
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || error;
  }
};

// Delete an announcement
export const deleteAnnouncement = async (announcementId, mintSpaceId) => {
  try {
    const response = await api.delete(`/announcements/${announcementId}`);
    return afterAnnouncementMutation(mintSpaceId, response.data);
  } catch (error) {
    throw error.response?.data || error;
  }
};


/** A linked page's title, description and picture, read by the server. */
export const getLinkPreview = async (url) => {
  const response = await api.get("/announcements/link-preview", { params: { url } });
  return response.data;
};
